import type React from 'react';
import { useState } from 'react';
import { useMissionControlStore } from '../store.js';
import type { BrowserTab, Lane, SelectedElement } from '../types.js';

interface BrowserSidecarProps {
  workspaceId: string | null;
  tabs: BrowserTab[];
  lanes: Lane[];
}

function deriveSelector(outerHtml: string): { selector: string; tag: string; text: string } {
  const tagMatch = outerHtml.match(/^<([a-zA-Z][a-zA-Z0-9]*)/);
  const tag = tagMatch?.[1]?.toLowerCase() ?? 'div';
  const idMatch = outerHtml.match(/id="([^"]+)"/);
  const classMatch = outerHtml.match(/class="([^"]+)"/);
  let selector = tag;
  if (idMatch?.[1]) {
    selector = `${tag}#${idMatch[1]}`;
  } else if (classMatch?.[1]) {
    const cls = classMatch[1].trim().split(/\s+/).slice(0, 2).join('.');
    selector = cls ? `${tag}.${cls}` : tag;
  }
  const textMatch = outerHtml.replace(/<[^>]+>/g, '').trim();
  return { selector, tag, text: textMatch.slice(0, 120) };
}

export function BrowserSidecar({ workspaceId, tabs, lanes }: BrowserSidecarProps) {
  const activeTabId = useMissionControlStore((s) => s.activeTabId);
  const openBrowserTab = useMissionControlStore((s) => s.openBrowserTab);
  const closeBrowserTab = useMissionControlStore((s) => s.closeBrowserTab);
  const setActiveTab = useMissionControlStore((s) => s.setActiveTab);
  const updateBrowserTab = useMissionControlStore((s) => s.updateBrowserTab);
  const setSelectedElement = useMissionControlStore((s) => s.setSelectedElement);
  const appendLaneLog = useMissionControlStore((s) => s.appendLaneLog);
  const emitEvent = useMissionControlStore((s) => s.emitEvent);

  const [urlInput, setUrlInput] = useState('http://localhost:3000');
  const [pasteInput, setPasteInput] = useState('');

  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];

  if (!workspaceId) {
    return (
      <section className="panel browser-sidecar" aria-label="Browser sidecar">
        <p className="sidecar-empty">Open a workspace to preview local URLs and target DOM elements.</p>
      </section>
    );
  }

  function handleOpen(event: React.FormEvent) {
    event.preventDefault();
    if (!workspaceId) return;
    const url = urlInput.trim();
    if (!url) return;
    const tab = openBrowserTab(workspaceId, url);
    setTimeout(() => updateBrowserTab(tab.id, { status: 'ready' }), 0);
  }

  function handlePickElement(event: React.FormEvent) {
    event.preventDefault();
    if (!activeTab) return;
    const snippet = pasteInput.trim();
    if (!snippet) return;
    const derived = deriveSelector(snippet);
    const selection: SelectedElement = {
      selector: derived.selector,
      tag: derived.tag,
      text: derived.text,
      outerHtml: snippet,
    };
    setSelectedElement(activeTab.id, selection);
  }

  function handleSendToLane(laneId: string) {
    if (!activeTab?.selectedElement || !workspaceId) return;
    const selection = activeTab.selectedElement;
    appendLaneLog(laneId, {
      text: `DOM target ${selection.selector}: ${selection.text.slice(0, 60)}`,
      tone: 'neutral',
    });
    emitEvent({
      workspaceId,
      laneId,
      kind: 'lane_assign',
      summary: `Browser element ${selection.selector} dispatched to lane`,
      payload: { selector: selection.selector, tag: selection.tag },
    });
  }

  return (
    <section className="panel browser-sidecar" aria-label="Browser sidecar">
      <div className="section-heading">
        <span className="section-kicker">Browser sidecar</span>
        <strong>Preview + DOM targeting</strong>
      </div>

      <form className="browser-url-bar" onSubmit={handleOpen}>
        <input
          type="text"
          value={urlInput}
          onChange={(event) => setUrlInput(event.target.value)}
          placeholder="http://localhost:3000"
          aria-label="URL to preview"
        />
        <button type="submit" className="primary-button">
          Open tab
        </button>
      </form>

      {tabs.length > 0 ? (
        <div className="browser-tab-strip" role="tablist">
          {tabs.map((tab) => (
            <div key={tab.id} className="browser-tab-wrap">
              <button
                type="button"
                role="tab"
                aria-selected={tab.id === activeTab?.id}
                className={`browser-tab ${tab.id === activeTab?.id ? 'browser-tab-active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="browser-tab-title">{tab.title}</span>
                <span className="browser-tab-status">{tab.status}</span>
              </button>
              <button
                type="button"
                className="browser-tab-close"
                aria-label={`Close ${tab.title}`}
                onClick={() => closeBrowserTab(tab.id)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {activeTab ? (
        <div className="browser-frame">
          <div className="browser-frame-chrome">
            <span className="browser-frame-url">{activeTab.url}</span>
            <span className="browser-frame-status">{activeTab.status}</span>
          </div>
          <div className="browser-frame-body">
            <iframe
              key={activeTab.id}
              title={`preview-${activeTab.title}`}
              src={activeTab.url}
              className="browser-frame-iframe"
              sandbox="allow-scripts allow-same-origin"
            />
          </div>

          <form className="dom-selector" onSubmit={handlePickElement}>
            <label>
              <span>Paste an outerHTML snippet to target an element</span>
              <textarea
                value={pasteInput}
                onChange={(event) => setPasteInput(event.target.value)}
                placeholder='<button class="primary" id="checkout">Buy</button>'
                rows={2}
                aria-label="DOM element HTML"
              />
            </label>
            <button type="submit" className="ghost-button">
              Lock onto element
            </button>
          </form>

          {activeTab.selectedElement ? (
            <div className="dom-selection-card" aria-label="Selected DOM element">
              <div className="dom-selection-row">
                <span className="dom-selection-tag">{activeTab.selectedElement.tag}</span>
                <code>{activeTab.selectedElement.selector}</code>
              </div>
              <p>{activeTab.selectedElement.text || '(no visible text)'}</p>
              <div className="dom-selection-dispatch">
                <span className="section-kicker">Send to lane</span>
                <div className="dom-selection-lanes">
                  {lanes.length === 0 ? (
                    <span className="dom-selection-empty">No lanes in this workspace.</span>
                  ) : (
                    lanes.map((lane) => (
                      <button
                        key={lane.id}
                        type="button"
                        className="dom-selection-lane"
                        onClick={() => handleSendToLane(lane.id)}
                        aria-label={`Send element to ${lane.name}`}
                      >
                        {lane.name}
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <p className="sidecar-empty">No tab open. Paste a local URL and click Open tab.</p>
      )}
    </section>
  );
}
